import type { ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * The filter row every transaction list screen carries.
 *
 * Twelve banking lists each wrote this out: a search box, a row of status pills, a from/to date
 * range with a clear. Byte for byte the same, down to `focus:ring-teal-500` — the signature of a
 * screen copied to make the next one. Twelve copies means the first person to restyle a filter
 * restyles one screen and leaves eleven behind.
 *
 * Deliberately not one `<ListFilterBar>` taking a dozen props. The screens genuinely differ in
 * which filters they carry — three have no date range, five no status pills — so these are separate
 * pieces a screen composes, and a screen that needs something else simply puts it in the row.
 */

/** The row itself: whatever filters a screen has, laid out the same way on each. */
export const FilterRow = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-3 mt-4 flex-wrap">{children}</div>
);

interface ListSearchProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter, and the search button if the screen has one. */
  onSubmit: () => void;
  /** Says what this screen searches: "Search deposit #, account, reference…". */
  placeholder: string;
}

export const ListSearch = ({ value, onChange, onSubmit, placeholder }: ListSearchProps) => (
  <div className="flex-1 max-w-xs relative">
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
      placeholder={placeholder}
      aria-label={placeholder}
      className={cn(
        'w-full pl-9 pr-3 py-2 text-sm border rounded-lg',
        'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700',
        'text-slate-900 dark:text-white',
        'focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none',
      )}
    />
  </div>
);

interface DateRangeProps {
  from: string | undefined;
  to: string | undefined;
  onChange: (range: { from?: string; to?: string }) => void;
  /**
   * Which date the range filters, where a screen has more than one worth filtering — the card
   * screens narrow by sale date, bank reconciliation by statement date. Omitted, the range is
   * simply From and To.
   */
  label?: string;
}

/**
 * From and To, with a clear that only appears once there is something to clear.
 *
 * The clear was a bare "✕" glyph on every screen. It is a real button with a name now, because a
 * screen reader announced the old one as the character itself.
 */
export const DateRangeFilter = ({ from, to, onChange, label }: DateRangeProps) => {
  const field = cn(
    'text-sm border rounded-lg px-2 py-1.5',
    'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700',
    'text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none',
  );

  return (
    <div className="flex items-center gap-2 ml-auto">
      {label && (
        <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{label}</span>
      )}
      <label htmlFor="filter-date-from" className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">From</label>
      <input
        id="filter-date-from"
        type="date"
        value={from ?? ''}
        onChange={e => onChange({ from: e.target.value || undefined, to })}
        className={field}
      />
      <label htmlFor="filter-date-to" className="text-xs text-slate-500 dark:text-slate-400">To</label>
      <input
        id="filter-date-to"
        type="date"
        value={to ?? ''}
        onChange={e => onChange({ from, to: e.target.value || undefined })}
        className={field}
      />
      {(from || to) && (
        <button
          type="button"
          aria-label="Clear the date range"
          title="Clear the date range"
          onClick={() => onChange({ from: undefined, to: undefined })}
          className="p-1 rounded text-slate-400 hover:text-red-500 transition-colors"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

interface StatusPillsProps<T extends string> {
  statuses: readonly T[];
  /** The one currently filtering, if any. */
  active: T | undefined;
  /** Clicking the active pill clears it, which is why this takes the value rather than a boolean. */
  onChange: (status: T | undefined) => void;
  /** What each status is called on screen. */
  label: (status: T) => string;
  /** The colours a status wears when it is the one selected. */
  activeClass: (status: T) => string;
}

export function StatusPills<T extends string>({
  statuses, active, onChange, label, activeClass,
}: StatusPillsProps<T>) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {statuses.map(status => {
        const selected = active === status;
        return (
          <button
            key={status}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(selected ? undefined : status)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-full transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
              selected
                ? `${activeClass(status)} ring-2 ring-offset-1 ring-current`
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200',
            )}
          >
            {label(status)}
          </button>
        );
      })}
    </div>
  );
}

export interface StatusTile<T extends string> {
  label: string;
  status: T;
  count: number;
  icon: ReactNode;
  /** Text colour for the icon and the count. */
  color: string;
  /** Tile background. */
  bg: string;
  /** Tile ring. */
  ring: string;
}

interface StatusTilesProps<T extends string> {
  tiles: readonly StatusTile<T>[];
  active: T | undefined;
  onChange: (status: T | undefined) => void;
}

/**
 * The counts along the top of a list, each one also a filter.
 *
 * They are buttons and always were, but only some screens said what clicking one would do. The
 * title is written here so every tile explains itself the same way.
 */
export function StatusTiles<T extends string>({ tiles, active, onChange }: StatusTilesProps<T>) {
  return (
    <div className={cn('grid gap-2 mt-4', `grid-cols-${tiles.length}`)} style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}>
      {tiles.map(tile => {
        const selected = active === tile.status;
        return (
          <button
            key={tile.label}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(selected ? undefined : tile.status)}
            title={selected ? `Clear the "${tile.label}" filter` : `Show only ${tile.label}`}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg ring-1 transition-all text-left',
              'hover:ring-2 hover:shadow-sm active:scale-95',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
              tile.bg, tile.ring,
              selected && 'ring-2 shadow-md scale-[0.98]',
            )}
          >
            <span className={tile.color}>{tile.icon}</span>
            <div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-none">{tile.label}</p>
              <p className={cn('text-sm font-bold leading-tight', tile.color)}>{tile.count}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
